import { v4 as uuidv4 } from "uuid";

export enum FieldType {
	BOOLEAN = "boolean",
	CALCULATION = "calculation",
	COMPETENCE_LIST = "competence_list",
	DATE = "date",
	DECIMAL = "decimal",
	GROUP = "group",
	INTEGER = "integer",
	IMAGE = "image",
	RATING = "rating",
	SELECT = "select",
	MULTI_SELECT = "multi_select",
	TEXT = "text",
	URL = "url",
	UPLOAD = "upload"
}

// Actual class, so we can do new!
export abstract class Field {
	uuid: string;
	name: string;
	readonly type: FieldType;
	required: boolean;

	constructor(name: string, type: FieldType, required: boolean) {
		this.uuid = uuidv4();
		this.name = name;
		this.type = type;
		this.required = required;
	}
}

export class BooleanField extends Field {
	value = false;

	constructor(name: string, required = false) {
		super(name, FieldType.BOOLEAN, required);
	}

	static copy(field: BooleanField): BooleanField {
		const duplicate = new BooleanField(field.name, field.required);
		duplicate.value = field.value;

		return duplicate;
	}
}

// TODO: = should not be part of the value.
export class CalculationField extends Field {
	value = "=";

	constructor(name: string, required = false) {
		super(name, FieldType.CALCULATION, required);
	}

	static copy(field: CalculationField): CalculationField {
		const duplicate = new CalculationField(field.name, field.required);
		duplicate.value = field.value;

		return duplicate;
	}
}

export type CompetenceListFieldLevel = {
	level: number;
	elements: Field[];
};

export class CompetenceListField extends Field {
	levels: CompetenceListFieldLevel[] = [];

	constructor(name: string, required = false) {
		super(name, FieldType.COMPETENCE_LIST, required);
	}

	static copy(field: CompetenceListField): CompetenceListField {
		const duplicate = new CompetenceListField(field.name, field.required);
		duplicate.levels = field.levels.map(level => ({
			level: level.level,
			elements: level.elements.map(element => copy(element))
		}));

		return duplicate;
	}
}

export class DateField extends Field {
	value = ""; // ISO 8601

	constructor(name: string, required = false) {
		super(name, FieldType.DATE, required);
	}

	static copy(field: DateField): DateField {
		const duplicate = new DateField(field.name, field.required);
		duplicate.value = field.value;

		return duplicate;
	}
}

export class DecimalField extends Field {
	value = 0.0;

	constructor(name: string, required = false) {
		super(name, FieldType.DECIMAL, required);
	}

	static copy(field: DecimalField): DecimalField {
		const duplicate = new DecimalField(field.name, field.required);
		duplicate.value = field.value;

		return duplicate;
	}
}

export class GroupField extends Field {
	children: Field[] = [];
	collapsed = false;

	constructor(name: string, required = false) {
		super(name, FieldType.GROUP, required);
	}

	static copy(field: GroupField): GroupField {
		const duplicate = new GroupField(field.name, field.required);
		duplicate.children = field.children.map(child => copy(child));
		duplicate.collapsed = field.collapsed;

		return duplicate;
	}
}

export class IntegerField extends Field {
	value = 0;

	constructor(name: string, required = false) {
		super(name, FieldType.INTEGER, required);
	}

	static copy(field: IntegerField): IntegerField {
		const duplicate = new IntegerField(field.name, field.required);
		duplicate.value = field.value;

		return duplicate;
	}
}

export class ImageField extends Field {
	value: number[] = [];

	constructor(name: string, required = false) {
		super(name, FieldType.IMAGE, required);
	}

	static copy(field: ImageField): ImageField {
		const duplicate = new ImageField(field.name, field.required);
		duplicate.value = [...field.value];

		return duplicate;
	}
}

export type SelectOption = {
	value: string;
	attachments: Array<number>;
};

export class SelectField extends Field {
	value = "";
	options: Array<SelectOption> = [];

	constructor(name: string, required = false) {
		super(name, FieldType.SELECT, required);
	}

	static copy(field: SelectField): SelectField {
		const duplicate = new SelectField(field.name, field.required);
		duplicate.value = field.value;
		duplicate.options = field.options.map(option => ({
			value: option.value,
			attachments: [...option.attachments]
		}));

		return duplicate;
	}
}

export class MultiSelectField extends Field {
	value: string[] = [];
	options: Array<SelectOption> = [];

	constructor(name: string, required = false) {
		super(name, FieldType.MULTI_SELECT, required);
	}

	static copy(field: MultiSelectField): MultiSelectField {
		const duplicate = new MultiSelectField(field.name, field.required);
		duplicate.value = [...field.value];
		duplicate.options = field.options.map(option => ({
			value: option.value,
			attachments: [...option.attachments]
		}));

		return duplicate;
	}
}

export type RatingInfo = {
	name: string;
	description: string;
	colour: string;
};

export class RatingField extends Field {
	value: number | null = null;
	override = false;
	info: RatingInfo[] = [];

	constructor(name: string, required = false) {
		super(name, FieldType.RATING, required);
	}

	static copy(field: RatingField): RatingField {
		const duplicate = new RatingField(field.name, field.required);
		duplicate.value = field.value;
		duplicate.override = field.override;
		duplicate.info = [...field.info];

		return duplicate;
	}
}

export class TextField extends Field {
	value = "";

	constructor(name: string, required = false) {
		super(name, FieldType.TEXT, required);
	}

	static copy(field: TextField): TextField {
		const duplicate = new TextField(field.name, field.required);
		duplicate.value = field.value;

		return duplicate;
	}
}

export class UrlField extends Field {
	value = "";

	constructor(name: string, required = false) {
		super(name, FieldType.URL, required);
	}

	static copy(field: UrlField): UrlField {
		const duplicate = new UrlField(field.name, field.required);
		duplicate.value = field.value;

		return duplicate;
	}
}

export class UploadField extends Field {
	value: number[] = [];

	constructor(name: string, required = false) {
		super(name, FieldType.UPLOAD, required);
	}

	static copy(field: UploadField): UploadField {
		const duplicate = new UploadField(field.name, field.required);
		duplicate.value = [...field.value];

		return duplicate;
	}
}

export function copy(field: Field): Field {
	switch (field.type) {
		case FieldType.BOOLEAN:
			return BooleanField.copy(field as BooleanField);
		case FieldType.CALCULATION:
			return CalculationField.copy(field as CalculationField);
		case FieldType.COMPETENCE_LIST:
			return CompetenceListField.copy(field as CompetenceListField);
		case FieldType.DATE:
			return DateField.copy(field as DateField);
		case FieldType.DECIMAL:
			return DecimalField.copy(field as DecimalField);
		case FieldType.GROUP:
			return GroupField.copy(field as GroupField);
		case FieldType.INTEGER:
			return IntegerField.copy(field as IntegerField);
		case FieldType.IMAGE:
			return ImageField.copy(field as ImageField);
		case FieldType.SELECT:
			return SelectField.copy(field as SelectField);
		case FieldType.MULTI_SELECT:
			return MultiSelectField.copy(field as MultiSelectField);
		case FieldType.RATING:
			return RatingField.copy(field as RatingField);
		case FieldType.TEXT:
			return TextField.copy(field as TextField);
		case FieldType.URL:
			return UrlField.copy(field as UrlField);
		case FieldType.UPLOAD:
			return UploadField.copy(field as UploadField);
	}
}
